import React, { useEffect, useState } from 'react';
import { ReelItem } from '../types';
import { waitingService } from '../services/waitingService';
import './MyCombinations.css';

interface MyCombinationsProps {
  userId: string;
}

export const MyCombinations: React.FC<MyCombinationsProps> = ({ userId }) => {
  const [creations, setCreations] = useState<ReelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreation, setSelectedCreation] = useState<ReelItem | null>(null);

  useEffect(() => {
    const fetchCreations = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      
      try {
        const data = await waitingService.getUserCreations(userId);
        setCreations(data);
      } catch (error) {
        console.error('Failed to load user creations', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreations();
  }, [userId]);

  const handleCreationClick = (creation: ReelItem) => {
    setSelectedCreation(creation);
  };

  const handleCloseModal = () => {
    setSelectedCreation(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency === 'TRY' ? 'TRY' : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleProductClick = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="my-combinations my-combinations--loading">
        <div className="my-combinations__spinner" />
        <p>Kombinleriniz yükleniyor...</p>
      </div>
    );
  }

  if (creations.length === 0) {
    return (
      <div className="my-combinations my-combinations--empty">
        <div className="my-combinations__empty-icon">🎨</div>
        <h3>Henüz Kombininiz Yok</h3>
        <p>Oluşturduğunuz kombinler burada listelenecek.</p>
      </div>
    );
  }

  return (
    <div className="my-combinations">
      <div className="my-combinations__grid">
        {creations.map((creation) => (
          <div 
            key={creation.id} 
            className="my-combinations__card"
            onClick={() => handleCreationClick(creation)}
          >
            <div className="my-combinations__media-wrapper">
              <video 
                src={`${creation.videoUrl}#t=0.1`} 
                className="my-combinations__media"
                muted
                playsInline
                loop
                preload="metadata"
                onMouseOver={(e) => {
                  const playPromise = e.currentTarget.play();
                  if (playPromise !== undefined) {
                    playPromise.catch(() => {});
                  }
                }}
                onMouseOut={(e) => e.currentTarget.pause()}
              />
              <div className="my-combinations__play-icon">▶</div>
            </div>
            {creation.product && (
              <div className="my-combinations__info">
                <span className="my-combinations__brand">{creation.product.brand}</span>
                <span className="my-combinations__date">{formatDate(creation.createdAt)}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedCreation && (
        <div className="my-combinations__modal-overlay" onClick={handleCloseModal}>
          <div className="my-combinations__modal" onClick={(e) => e.stopPropagation()}>
            <button className="my-combinations__modal-close" onClick={handleCloseModal}>✕</button>
            
            <div className="my-combinations__modal-content">
              <div className="my-combinations__modal-media-container">
                <video 
                  src={selectedCreation.videoUrl} 
                  className="my-combinations__modal-video"
                  controls
                  autoPlay
                  playsInline
                />
              </div>
              
              {selectedCreation.product && (
                <div className="my-combinations__modal-details">
                  <div className="my-combinations__product-header">
                    <img 
                      src={selectedCreation.product.imageUrl} 
                      alt={selectedCreation.product.name} 
                      className="my-combinations__product-thumb"
                    />
                    <div className="my-combinations__product-info">
                      <span className="my-combinations__product-brand">{selectedCreation.product.brand}</span>
                      <h3 className="my-combinations__product-name">{selectedCreation.product.name}</h3>
                      <span className="my-combinations__product-price">
                        {formatPrice(selectedCreation.product.price, selectedCreation.product.currency)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="my-combinations__creation-meta">
                    <span>Oluşturulma Tarihi:</span>
                    <span>{formatDate(selectedCreation.createdAt)}</span>
                  </div>

                  <button 
                    className="my-combinations__cta-button"
                    onClick={() => handleProductClick(selectedCreation.product?.url)}
                    disabled={!selectedCreation.product.url}
                  >
                    Ürüne Git
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCombinations;
